import type { FolderList, FolderSection, ProjectDetail as Project } from "@esi/shared";
import { MAX_FOLDER_DEPTH } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Ellipsis,
  FileText,
  FolderClosed,
  FolderInput,
  FolderOpen,
  FolderPlus,
  GitPullRequestArrow,
  Library,
  ListChecks,
  Pencil,
  PlugZap,
  Search,
  Shapes,
  ShieldAlert,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Notice } from "@/components/Notice";
import { TreeView, treeRowElement } from "@/components/tree/Tree";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, foldTitle } from "@/lib/utils";
import { ArtefactRow } from "../conversation/artefacts/ArtefactRow.tsx";
import { type ConversationScope, useArtefacts } from "../conversation/artefacts/queries.ts";
import { useAttachmentContext } from "../conversation/attachments/AttachmentControls.tsx";
import {
  AttachmentRow,
  attachmentRowActions,
  useAttachmentDeleteFocus,
} from "../conversation/attachments/AttachmentRow.tsx";
import { attachmentDownloadHref } from "../conversation/attachments/api.ts";
import { formatDate } from "../i18n/format.ts";
import {
  Link,
  navigate,
  useOpenArtefact,
  useOpenWiki,
  useSearch,
  wikiChangesPath,
  wikiPath,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { usePendingPages } from "../wiki/PendingHints.tsx";
import { useChangesInbox, useWikiIndex } from "../wiki/queries.ts";
import { can, canChange } from "./capabilities.ts";
import { downloadHref } from "./download.ts";
import { FileRowBody, OUTSIDER_SETTINGS, useFileRows, useFileSettings } from "./FileRow.tsx";
import { type FolderDialog, FolderDialogs } from "./FolderDialogs.tsx";
import { pathText } from "./FolderPicker.tsx";
import { fileActions } from "./file-actions.ts";
import { type FolderWriteResult, folderKeys, useFolders } from "./folders.ts";
import {
  FOLDABLE_DEFAULT_OPEN,
  type FolderNode,
  folderNodeId,
  folderPath,
  type ItemNode,
  knowledgeTree,
  knownState,
  rememberedTreeState,
  rememberTreeState,
  searchTree,
  sectionId,
  type KnowledgeTree as Tree,
  type TreeNode,
  type TreeRow,
  type TreeSection,
  visibleRows,
} from "./knowledge-tree.ts";
import { useDocuments, useMembers, usePrincipals } from "./queries.ts";
import { useOpenSource } from "./SourceViewer.tsx";
import { UploadDialog } from "./UploadDialog.tsx";

type Row = TreeRow & { id: string };

const ITEM_ICON = {
  file: FileText,
  wiki: BookOpen,
  connected: PlugZap,
  artefact: Shapes,
} as const;
const SECTION_ICON = {
  files: FileText,
  wiki: BookOpen,
  connected: PlugZap,
  artefacts: Shapes,
  attachments: FileText,
} as const;
/** Indentation per level, and the folder level after which it stops growing (design §6). */
const STEP_PX = 16;
const STEP_NARROW_PX = 12;
const INDENT_FOLDER_DEPTH = 3;

/**
 * The knowledge tree (openspec change `knowledge-panel`): beside a conversation every section
 * (`scope` "all"), on the project home one section at full width. Everything shown came from
 * the API for this viewer; folders and counts only organise it. Wide screens expand folders in
 * place; narrow ones (and the sheet) open a folder as the tree's root (design §6).
 */
export function KnowledgeTree(props: {
  project: Project;
  scope: "all" | FolderSection;
  /** Drill-in instead of expand in place (narrow screens and the sheet). */
  narrow: boolean;
  /** The project home's Files tab on a wide screen: details as columns. */
  columns?: boolean;
  /** `?folder=` of the project home: the folder to open, kept in the URL. */
  folderParam?: string | null;
  onFolderParam?: (folderId: string | null) => void;
  /** The page's scroll container when the tree does not scroll on its own. */
  scrollRef?: RefObject<HTMLElement | null>;
  /** Opens the upload dialog into a folder (the panel's header shares it). */
  upload: { open: (folderId: string | null) => void } | null;
  /** Controls beside the search (the sheet's Upload, which has no header bar). */
  toolbar?: ReactNode;
  /** The item open beside the tree (the wiki page shown), marked `aria-selected`. */
  selectedId?: string | null;
  /**
   * The item a link points at (`?document=` of an MCP client's citation, mcp-server-core R5):
   * revealed once it is known and marked `aria-current`; opening it stays the usual per-click,
   * access-checked download.
   */
  linkedId?: string | null;
  /** Replaces the empty state of a tree with nothing to show (the wiki tab's own words). */
  empty?: ReactNode;
  /**
   * Narrows the tree to the items it accepts, in their folders (the Sources tab's `?status=`),
   * as a search does; `onlyEmpty` replaces the no-match state when nothing is accepted.
   */
  only?: ((item: ItemNode) => boolean) | null;
  onlyEmpty?: ReactNode;
  /** Drops `only`: a write's result it hides (an upload, a folder) is then shown and focused. */
  onClearOnly?: () => void;
  /** The drilled folder (narrow screens), where an upload from the header starts. */
  onRootChange?: (folderId: string | null) => void;
  /** The open conversation: its artefacts fill the Artefacts section (`artefacts-viewer`). */
  conversation?: ConversationScope | null;
  onOpenArtefact?: (artefactId: string, version: number) => void;
}) {
  const { project, scope, narrow } = props;
  const { t } = useTranslation();
  const { userId } = useSession();
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const showFiles = scope !== "wiki";
  const showWiki = scope !== "files";
  // Only the lists this tree shows are read (the Files tab never needs the wiki index).
  const documents = useDocuments(project.id, showFiles);
  const index = useWikiIndex(project.id, showWiki);
  const folders = useFolders(project.id);
  const conversation = scope === "all" ? (props.conversation ?? null) : null;
  const artefactList = useArtefacts(
    conversation ?? { projectId: null, conversationId: "" },
    conversation !== null,
  );
  const attachmentContext = useAttachmentContext();
  const attachmentList =
    conversation && attachmentContext?.conversationId === conversation.conversationId
      ? attachmentContext.list
      : undefined;
  const attachments = attachmentList?.attachments;
  const { show } = useOpenArtefact();
  const showArtefact = (open: { artefactId: string; version: number }) =>
    props.onOpenArtefact ? props.onOpenArtefact(open.artefactId, open.version) : show(open);
  const settings = useFileSettings(project, documents.data, showFiles);
  const canManage = canChange(project, "folder.manage");
  const canUpload = canChange(project, "file.upload");
  const canReview = canChange(project, "wiki.draft");
  const queryClient = useQueryClient();

  // Each list fails on its own: only a first load that failed is an error (a failed refetch
  // keeps what is shown). File rows need the settings (levels, OCR cap).
  const failures = [
    ["documents", showFiles && documents.isError && !documents.data ? documents.error : null],
    ["settings", showFiles && settings.isError && !settings.data ? settings.error : null],
    ["wiki", showWiki && index.isError && !index.data ? index.error : null],
    ["folders", folders.isError && !folders.data ? folders.error : null],
  ] as const;
  const failed = failures.filter(([, e]) => e !== null) as [string, Error][];
  const files = showFiles && documents.data && settings.data ? documents.data.documents : undefined;
  const pages = showWiki ? index.data?.pages : undefined;
  const folderList = folders.data?.folders;
  const loaded =
    (!showFiles || files !== undefined || documents.isError || settings.isError) &&
    (!showWiki || pages !== undefined || index.isError) &&
    (folderList !== undefined || folders.isError);
  const artefacts = artefactList.data?.artefacts;
  const tree = useMemo(
    () => knowledgeTree({ documents: files, pages, folders: folderList, artefacts, attachments }),
    [files, pages, folderList, artefacts, attachments],
  );
  const sections = useMemo<TreeSection[] | null>(() => (scope === "all" ? null : [scope]), [scope]);
  // On the project home the tree starts inside its section: no section row.
  const base = scope === "all" ? null : sectionId(scope);

  // Expansion and the drilled root, kept per project in this browser (design §10).
  const [kept, setKept] = useState(() => rememberedTreeState(project.id));
  const [focusedId, setFocusedId] = useState<string | null>(kept.focus);
  const state = useMemo(
    () => (loaded && failed.length === 0 ? knownState(tree, kept) : kept),
    [loaded, failed.length, tree, kept],
  );
  // Artefacts is expanded unless folded (`collapsed`): it comes and goes with the conversation.
  // Conversation files open expanded; App keys ProjectView per conversation, so a new one does too.
  const [attachmentOpen, setAttachmentOpen] = useState(true);
  const collapsed = state.collapsed ?? [];
  const expanded = useMemo(
    () =>
      new Set([
        ...state.expanded,
        ...(attachmentOpen ? ["s:attachments"] : []),
        ...FOLDABLE_DEFAULT_OPEN.filter((nodeId) => !collapsed.includes(nodeId)),
      ]),
    [state.expanded, collapsed, attachmentOpen],
  );
  const keep = (next: {
    expanded?: Iterable<string>;
    root?: string | null;
    collapsed?: string[];
  }) => {
    const value = {
      expanded: next.expanded
        ? [...new Set(next.expanded)].filter(
            (nodeId) => !FOLDABLE_DEFAULT_OPEN.includes(nodeId) && nodeId !== "s:attachments",
          )
        : state.expanded,
      root: next.root !== undefined ? next.root : state.root,
      collapsed: next.collapsed ?? collapsed,
    };
    setKept({ ...value, focus: null });
    rememberTreeState(project.id, value);
  };
  // The kept root is per project: only a folder of a section this tree shows is opened.
  const inScope = (node: TreeNode | undefined) =>
    node !== undefined && (scope === "all" || node.section === scope);
  const drilled = narrow && state.root && inScope(tree.byId.get(state.root)) ? state.root : null;
  const onRootChange = props.onRootChange;
  // Where a header upload starts: the drilled folder when it is a Files folder, else the root.
  const drilledFolder = drilled ? tree.byId.get(drilled) : undefined;
  const uploadRoot =
    drilledFolder?.kind === "folder" && drilledFolder.section === "files"
      ? drilledFolder.folder.id
      : null;
  useEffect(() => {
    onRootChange?.(uploadRoot);
  }, [uploadRoot, onRootChange]);

  // Search: matches in place with their path; toggles made while searching are its own.
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState<ReadonlyMap<string, boolean>>(new Map());
  const beforeSearch = useRef<string | null>(null);
  const only = props.only ?? null;
  const search = useMemo(() => searchTree(tree, query, only), [tree, query, only]);
  const open = useMemo(() => {
    if (!search) {
      // Narrow screens drill into folders: only sections expand in place.
      return narrow
        ? new Set([...expanded].filter((nodeId) => tree.byId.get(nodeId)?.kind === "section"))
        : expanded;
    }
    const all = new Set([...expanded, ...search.expanded]);
    for (const [nodeId, on] of searchOpen) {
      on ? all.add(nodeId) : all.delete(nodeId);
    }
    return all;
  }, [expanded, search, searchOpen, narrow, tree]);
  // Matching names in what this tree shows (folders too), for the announcement.
  const needle = foldTitle(query.trim());
  const shownMatches = search
    ? [...search.shown].filter((nodeId) => {
        const node = tree.byId.get(nodeId);
        return (
          node !== undefined &&
          node.kind !== "section" &&
          (sections ?? tree.sections.map((x) => x.section)).includes(node.section) &&
          (only ? search.matches.has(nodeId) : node.folded.includes(needle))
        );
      }).length
    : 0;
  const rows = useMemo(
    () =>
      // A search looks through the whole tree (or section); clearing it returns to the root.
      visibleRows(
        tree,
        open,
        search ? base : (drilled ?? base),
        search && { ...search, expanded: new Set() },
        sections,
      ).map((r) => ({ ...r, id: r.node.id })),
    [tree, open, drilled, base, search, sections],
  );

  const toggle = (node: TreeNode, on: boolean) => {
    if (search) {
      setSearchOpen((was) => new Map(was).set(node.id, on));
      return;
    }
    if (node.section === "attachments") {
      setAttachmentOpen(on);
      return;
    }
    if (FOLDABLE_DEFAULT_OPEN.includes(node.id)) {
      keep({
        collapsed: on ? collapsed.filter((x) => x !== node.id) : [...collapsed, node.id],
      });
      return;
    }
    const next = new Set(expanded);
    on ? next.add(node.id) : next.delete(node.id);
    keep({ expanded: next });
    // Collapsing touches the URL only when it hides the folder written there.
    if (node.kind === "folder" && (on || hidesParam(node))) {
      setFolderParam(on ? node.folder.id : parentFolderId(tree, node));
    }
  };
  const hidesParam = (node: FolderNode) => {
    const param = props.folderParam ? tree.byId.get(folderNodeId(props.folderParam)) : undefined;
    return (
      param?.kind === "folder" &&
      (param.id === node.id || folderPath(tree, param).some((f) => f.id === node.id))
    );
  };

  // `?folder=` (a copied link, a reload): open that folder once it is known (design §11).
  const [reveal, setReveal] = useState<{ id: string; at: number } | null>(null);
  const applied = useRef<string | null>(null);
  // A folder the tree opened itself goes into the URL as already applied: collapsing one must
  // not move focus to the parent written there.
  const setFolderParam = (folderId: string | null) => {
    applied.current = `${folderId}:${narrow}`;
    props.onFolderParam?.(folderId);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per folder and layout
  useEffect(() => {
    const param = props.folderParam;
    const key = `${param}:${narrow}`;
    if (!param || !loaded || applied.current === key) {
      return;
    }
    const node = tree.byId.get(folderNodeId(param));
    if (node?.kind !== "folder" || !inScope(node)) {
      return;
    }
    applied.current = key;
    if (narrow) {
      keep({ root: node.id });
    } else {
      keep({ expanded: [...state.expanded, ...folderPath(tree, node).map((f) => f.id), node.id] });
      setFocusedId(node.id);
      setReveal({ id: node.id, at: Date.now() });
    }
  }, [props.folderParam, loaded, tree, narrow]);

  // Widening the window shows the whole tree with the drilled folder open and focused. A wide
  // tree mounted with a drilled root kept (the sheet gave way to the docked panel) opens it
  // too, as the tab stop only: nothing else moved, so focus stays where it is.
  const wasNarrow = useRef<boolean | "mounted">(narrow || "mounted");
  // biome-ignore lint/correctness/useExhaustiveDependencies: on a layout change or the first load
  useEffect(() => {
    if (wasNarrow.current && !narrow && state.root) {
      if (!loaded) {
        return;
      }
      // A folder link (`?folder=`) wins over the root remembered from another visit.
      if (wasNarrow.current === "mounted" && props.folderParam) {
        wasNarrow.current = narrow;
        return;
      }
      const node = tree.byId.get(state.root);
      if (node?.kind === "folder" && inScope(node)) {
        keep({
          expanded: [
            ...state.expanded,
            sectionId(node.section),
            ...folderPath(tree, node).map((f) => f.id),
            node.id,
          ],
          root: null,
        });
        setFocusedId(node.id);
        if (wasNarrow.current === true) {
          setReveal({ id: node.id, at: Date.now() });
        }
      }
    }
    wasNarrow.current = narrow;
  }, [narrow, loaded]);

  const drill = (node: FolderNode | null, from?: string) => {
    keep({ root: node?.id ?? null });
    setFolderParam(node?.folder.id ?? null);
    const target = from ?? node?.children[0]?.id;
    if (target) {
      setFocusedId(target);
      setReveal({ id: target, at: Date.now() });
    } else if (node) {
      // An empty folder has no row to hold focus: its Back button does.
      setFocusBack(Date.now());
    }
  };
  const backButton = useRef<HTMLButtonElement>(null);
  const [focusBack, setFocusBack] = useState(0);
  useEffect(() => {
    if (focusBack) {
      backButton.current?.focus();
    }
  }, [focusBack]);
  const back = () => {
    const node = drilled ? tree.byId.get(drilled) : undefined;
    if (node?.kind !== "folder") {
      return;
    }
    const parent = node.parentId ? tree.byId.get(node.parentId) : undefined;
    drill(parent?.kind === "folder" ? parent : null, node.id);
  };

  // Row menus: one open at a time, opened by its button or from the row's keyboard.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [dialog, setDialog] = useState<FolderDialog | null>(null);
  // Where focus goes when a dialog closes: the node a write revealed, else the row it opened from.
  const opener = useRef<string | null>(null);
  const landing = useRef<string | null>(null);
  const rowElement = (nodeId: string) => treeRowElement(container.current, nodeId);
  const deleteFocus = useAttachmentDeleteFocus(rows, container, setFocusedId);
  const openDialog = (next: FolderDialog, from: string) => {
    opener.current = from;
    landing.current = null;
    setDialog(next);
  };
  const dialogReturn = () =>
    (landing.current ? rowElement(landing.current) : null) ??
    (opener.current ? rowElement(opener.current) : null);
  const onDone = (result: FolderWriteResult) => {
    const target =
      result.op === "delete"
        ? null
        : result.op === "moveItem"
          ? `${result.section === "wiki" ? "w" : "d"}:${result.moved.documentId}`
          : folderNodeId(result.folder.id);
    if (result.op === "delete") {
      // Their new names: the folders were read again before this runs (the tree is older).
      const fresh = queryClient.getQueryData<FolderList>(folderKeys.list(project.id))?.folders;
      const renamed = result.result.renamed
        .map((folderId) => fresh?.find((f) => f.id === folderId)?.name)
        .filter((name): name is string => Boolean(name));
      toast(
        renamed.length > 0
          ? t("folders.delete.doneRenamed", { names: renamed.join(", ") })
          : t("folders.delete.done"),
      );
      const gone = dialog?.kind === "delete" ? dialog.folder : null;
      const parent = gone?.parentId ?? null;
      // A top-level folder's parent is its section, which has no row on the project home, and
      // the drilled folder shows in the breadcrumb only: then the first row still there.
      const parentId =
        parent && parent !== base && parent !== drilled
          ? parent
          : (rows.find((r) => r.id !== gone?.id)?.id ?? null);
      if (!parentId && drilled) {
        setFocusBack(Date.now());
      }
      if (parentId) {
        // Revealed as a write's result is: through a search that no longer shows it, too.
        landing.current = parentId;
        setPendingReveal(parentId);
      }
      return;
    }
    if (target) {
      // Revealed once the tree holds the new state: its ancestors open, then focus.
      landing.current = target;
      setPendingReveal(target);
    }
  };
  const [pendingReveal, setPendingReveal] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the refetched tree has it
  useEffect(() => {
    const node = pendingReveal ? tree.byId.get(pendingReveal) : undefined;
    if (!pendingReveal || !node) {
      return;
    }
    setPendingReveal(null);
    const ancestors = folderPath(tree, node).map((f) => f.id);
    // A result the search no longer shows (renamed or moved away from the match): the search
    // goes, so the result can be shown and focused.
    if (search && !search.shown.has(node.id)) {
      setQuery("");
      setSearchOpen(new Map());
      if (only) {
        props.onClearOnly?.();
      }
    }
    if (narrow) {
      const parent = node.parentId ? tree.byId.get(node.parentId) : undefined;
      const folder = parent?.kind === "folder" ? parent : null;
      // A section root's item needs its section open (the sheet lists all sections).
      keep({ root: folder?.id ?? null, expanded: [...state.expanded, sectionId(node.section)] });
      // The URL follows the folder now shown, as a drill does.
      setFolderParam(folder?.folder.id ?? null);
    } else {
      keep({ expanded: [...state.expanded, sectionId(node.section), ...ancestors] });
    }
    setFocusedId(node.id);
    setReveal({ id: node.id, at: Date.now() });
  }, [pendingReveal, tree]);
  // A linked item is revealed once per link, when the loaded tree holds it.
  const linkedApplied = useRef<string | null>(null);
  useEffect(() => {
    const linked = props.linkedId;
    if (!linked || !loaded || linkedApplied.current === linked || !tree.byId.has(linked)) {
      return;
    }
    linkedApplied.current = linked;
    setPendingReveal(linked);
  }, [props.linkedId, loaded, tree]);

  const restricted = showFiles ? (documents.data?.restricted ?? null) : null;
  const reviewCount = useChangesInbox(project, canReview && showWiki).inbox?.count ?? 0;
  const pending = scope === "wiki" ? 0 : reviewCount;
  // Pages a pending change touches (#411), and the folders holding one: in the wiki's own Pages
  // panel only, while its Pending changes switch is on, from what the viewer may see.
  const pendingPages = usePendingPages(project.id, scope === "wiki");
  const pendingFolders = useMemo(() => {
    const ids = new Set<string>();
    if (pendingPages.size === 0) {
      return ids;
    }
    for (const page of tree.byId.values()) {
      if (
        page.kind !== "item" ||
        page.type !== "wiki" ||
        !pendingPages.has(page.page.id.toLowerCase())
      ) {
        continue;
      }
      let parent = page.parentId;
      for (let node = parent ? tree.byId.get(parent) : undefined; node?.kind === "folder"; ) {
        ids.add(node.id);
        parent = node.parentId;
        node = parent ? tree.byId.get(parent) : undefined;
      }
    }
    return ids;
  }, [pendingPages, tree]);
  const fileSettings = settings.data ?? OUTSIDER_SETTINGS;
  const { facts, overlays } = useFileRows(project, fileSettings);
  const sectionName = (section: TreeSection) => t(`knowledgePanel.section.${section}`);
  // Nothing readable and no folder to browse: one quiet empty state, no search (#111 note).
  const holds = (section: TreeSection) =>
    (tree.byId.get(sectionId(section)) as { children: unknown[] } | undefined)?.children.length;
  const noSources =
    loaded &&
    failed.length === 0 &&
    !(showFiles && holds("files")) &&
    !(showWiki && holds("wiki")) &&
    !tree.hasConnected &&
    !holds("artefacts") &&
    !holds("attachments");

  const openSource = useOpenSource();
  const { show: openWikiPanel } = useOpenWiki();
  const activate = (row: Row, via: "keyboard" | "pointer") => {
    const node = row.node;
    if (node.kind === "section") {
      toggle(node, !row.expanded);
    } else if (node.kind === "folder") {
      if (narrow && !search) {
        drill(node);
      } else {
        toggle(node, !row.expanded);
      }
    } else if (node.type === "wiki") {
      // Beside a conversation, the page opens in its wiki panel (wiki-collaboration D8a).
      if (conversation) {
        openWikiPanel({ kind: "page", pageId: node.page.id.toLowerCase(), version: null });
      } else {
        navigate(wikiPath(project.id, node.page.id));
      }
    } else if (node.type === "artefact") {
      // Its latest readable version, beside the conversation (the switcher has the others).
      showArtefact({ artefactId: node.artefact.id, version: node.artefact.version });
    } else if (node.type === "attachment") {
      const actions = conversation ? attachmentRowActions(node.attachment, attachmentList) : null;
      // A compatible file opens (tab or viewer), on a click too, like an artefact; any other
      // downloads from the keyboard only (a cite-only file has no default action).
      if (conversation && actions?.open) {
        openSource(
          {
            kind: "attachment",
            conversationId: conversation.conversationId,
            attachmentId: node.attachment.id,
            title: node.attachment.title,
          },
          node.attachment.mediaType,
        );
      } else if (via === "keyboard" && conversation && actions?.download) {
        window.open(
          attachmentDownloadHref(conversation.conversationId, node.attachment.id),
          "_blank",
          "noopener,noreferrer",
        );
      }
    } else {
      const level = fileSettings.scheme.find((l) => l.id === node.document.classification);
      const actions = fileActions(node.document, project, userId, level);
      if (actions.includes("open")) {
        openSource(
          {
            kind: "document",
            projectId: project.id,
            documentId: node.document.id,
            title: node.document.title,
          },
          node.document.mediaType,
        );
      } else if (via === "keyboard" && actions.includes("download")) {
        window.open(downloadHref(project.id, node.document.id), "_blank", "noopener,noreferrer");
      }
    }
  };

  const menuOf = (row: Row) => ({
    open: menuFor === row.id,
    onOpenChange: (on: boolean) => setMenuFor(on ? row.id : null),
    returnFocus: () => rowElement(row.id),
  });

  const pathOf = (node: TreeNode) =>
    pathText(
      sectionName(node.section),
      folderPath(tree, node).map((f) => f.name),
    );

  const nameWithMatch = (name: string) =>
    search ? <Highlight text={name} query={search.query} /> : name;

  // Indentation grows up to folder depth 3 (and the items inside), then stays (design §6).
  const capSteps = INDENT_FOLDER_DEPTH - 1 + (scope === "all" && !drilled ? 1 : 0);
  const stepsOf = (row: Row) => row.level - 1;
  const capOf = (row: Row) => capSteps + (row.node.kind === "item" ? 1 : 0);
  const indent = (row: Row) =>
    (narrow ? STEP_NARROW_PX : STEP_PX) * Math.max(Math.min(stepsOf(row), capOf(row)), 0);
  /** Rows past the cap show the hidden part of their path ("… › Seals ›"). */
  const hiddenPrefix = (row: Row) => {
    if (stepsOf(row) <= capOf(row) || search) {
      return null;
    }
    const parent = row.node.parentId ? tree.byId.get(row.node.parentId) : undefined;
    return parent?.kind === "folder" ? parent.name : null;
  };

  const renderRow = (row: Row) => {
    const node = row.node;
    // Items keep the chevron's gutter, so their icon lines up with their sibling folders'.
    const pad = { paddingLeft: `${8 + indent(row) + (node.kind === "item" ? 26 : 0)}px` };
    if (node.kind === "section") {
      return (
        <SectionRow
          row={row}
          name={sectionName(node.section)}
          labelId={`${id}-${row.id}-name`}
          detailsId={`${id}-${row.id}-details`}
          restricted={node.section === "files" ? restricted : null}
          wiki={
            node.section === "wiki"
              ? { projectId: project.id, pending: canReview ? pending : 0 }
              : null
          }
          menu={
            <SectionMenu
              {...menuOf(row)}
              label={t("knowledgePanel.actionsFor", { name: sectionName(node.section) })}
              section={node.section}
              canManage={canManage && (node.section === "files" || node.section === "wiki")}
              canUpload={canUpload && node.section === "files" && props.upload !== null}
              pending={canReview && node.section === "wiki" ? pending : 0}
              projectId={project.id}
              onCreate={() =>
                openDialog(
                  { kind: "create", section: node.section as FolderSection, parent: null },
                  row.id,
                )
              }
              onUpload={() => props.upload?.open(null)}
            />
          }
        />
      );
    }
    if (node.kind === "folder") {
      const prefix = hiddenPrefix(row);
      const Icon = row.expanded ? FolderOpen : FolderClosed;
      // A quiet dot while folded: an open folder shows its pages' own marks.
      const folderPending = !row.expanded && pendingFolders.has(node.id);
      return (
        <span className="flex min-h-8 items-center gap-1.5 py-1 pr-1.5 text-sm" style={pad}>
          <Chevron expanded={row.expanded} empty={node.children.length === 0} />
          <Icon aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.5} />
          <span
            className="flex min-w-0 flex-1 items-baseline gap-1"
            title={pathText(
              sectionName(node.section),
              [...folderPath(tree, node), node].map((f) => f.name),
            )}
          >
            {prefix ? (
              // The name keeps the room: the hidden path takes at most half and truncates first.
              <span
                aria-hidden="true"
                className="max-w-[45%] min-w-0 truncate text-xs text-fg-muted"
              >
                … › {prefix} ›
              </span>
            ) : null}
            <span id={`${id}-${row.id}-name`} className="min-w-0 flex-1 truncate text-fg">
              {nameWithMatch(node.name)}
            </span>
          </span>
          {folderPending ? (
            <span
              aria-hidden="true"
              data-pending-folder=""
              className="size-1.5 shrink-0 rounded-full bg-warning-border"
            />
          ) : null}
          <span
            id={`${id}-${row.id}-details`}
            className="shrink-0 px-1 text-xs text-fg-muted tabular-nums"
          >
            <span aria-hidden="true">{row.count}</span>
            <span className="sr-only">
              {search
                ? t("knowledgePanel.matches", { count: row.count ?? 0 })
                : t("knowledgePanel.items", { count: row.count ?? 0 })}
              {", "}
              {pathOf(node)}
              {folderPending ? `, ${t("knowledgePanel.pendingFolder")}` : null}
            </span>
          </span>
          {canManage || (canUpload && node.section === "files") ? (
            <FolderMenu
              {...menuOf(row)}
              label={t("knowledgePanel.actionsFor", { name: node.name })}
              node={node}
              canManage={canManage}
              canUpload={canUpload && node.section === "files" && props.upload !== null}
              onCreate={() =>
                openDialog({ kind: "create", section: node.folder.section, parent: node }, row.id)
              }
              onRename={() => openDialog({ kind: "rename", folder: node }, row.id)}
              onMove={() => openDialog({ kind: "move", node }, row.id)}
              onDelete={() => openDialog({ kind: "delete", folder: node }, row.id)}
              onUpload={() => props.upload?.open(node.folder.id)}
            />
          ) : null}
        </span>
      );
    }
    if (node.type === "attachment" && conversation) {
      return (
        <AttachmentRow
          conversationId={conversation.conversationId}
          attachment={node.attachment}
          list={attachmentList}
          style={pad}
          name={nameWithMatch(node.name)}
          nameId={`${id}-${row.id}-name`}
          detailsId={`${id}-${row.id}-details`}
          menu={(children) => (
            <ItemMenu {...menuOf(row)} label={t("files.actionsFor", { title: node.name })}>
              {children}
            </ItemMenu>
          )}
          returnFocus={() => rowElement(row.id)}
          onDeleted={() => deleteFocus.deleted(row.id)}
          deleteReturnFocus={() => deleteFocus.returnFocus(row.id)}
        />
      );
    }
    if (node.type === "artefact") {
      return (
        <ArtefactRow
          artefact={node.artefact}
          scope={conversation ?? { projectId: project.id, conversationId: "" }}
          style={pad}
          name={nameWithMatch(node.name)}
          nameId={`${id}-${row.id}-name`}
          detailsId={`${id}-${row.id}-details`}
          menu={(children) => (
            <ItemMenu {...menuOf(row)} label={t("files.actionsFor", { title: node.name })}>
              {children}
            </ItemMenu>
          )}
          onOpen={() =>
            showArtefact({ artefactId: node.artefact.id, version: node.artefact.version })
          }
          canAdd={Boolean(conversation) && canUpload}
          returnFocus={() => menuOf(row).returnFocus()}
        />
      );
    }
    if (node.type === "attachment") {
      return null;
    }
    const prefix = hiddenPrefix(row);
    const Icon = ITEM_ICON[node.type];
    // In the wiki's own Pages panel every item is a page: no type word (user decision, #411).
    const onlyPages = scope === "wiki" && node.type === "wiki";
    const leading = (
      <>
        <Icon
          aria-hidden="true"
          className={cn("mr-1.5 size-4 shrink-0 text-fg-muted", !onlyPages && "mt-0.5")}
          strokeWidth={1.5}
        />
        {onlyPages ? null : (
          <span className="sr-only">{t(`knowledgePanel.type.${node.type}`)}: </span>
        )}
      </>
    );
    // Inline with the title, so a long one wraps under it instead of into a narrow column.
    const title = (
      <>
        {prefix ? (
          <span aria-hidden="true" className="mr-1 text-xs font-normal text-fg-muted">
            … › {prefix} ›
          </span>
        ) : null}
        {nameWithMatch(node.name)}
      </>
    );
    const path = search ? (
      <span className="truncate text-xs text-fg-muted" title={pathOf(node)}>
        {pathOf(node)}
      </span>
    ) : (
      <span className="sr-only">{pathOf(node)}</span>
    );
    const moveItem =
      canManage && node.type !== "connected" ? (
        <DropdownMenuItem onSelect={() => openDialog({ kind: "move", node }, row.id)}>
          <FolderInput aria-hidden="true" strokeWidth={1.75} />
          {t("folders.moveTo")}
        </DropdownMenuItem>
      ) : null;
    const wikiMenu =
      node.type === "wiki" && moveItem ? (
        <ItemMenu {...menuOf(row)} label={t("files.actionsFor", { title: node.name })}>
          <DropdownMenuItem asChild>
            <Link href={wikiPath(project.id, node.page.id)}>
              <BookOpen aria-hidden="true" strokeWidth={1.75} />
              {t("knowledgePanel.openPage")}
            </Link>
          </DropdownMenuItem>
          {moveItem}
        </ItemMenu>
      ) : null;
    if (node.type === "wiki" && scope === "wiki") {
      return (
        <WikiPageRow
          name={node.name}
          title={title}
          leading={leading}
          nameId={`${id}-${row.id}-name`}
          detailsId={`${id}-${row.id}-details`}
          updated={formatDate(node.page.updatedAt)}
          declassified={!!node.page.declassified && can(project, "declassify")}
          pending={pendingPages.has(node.page.id.toLowerCase())}
          path={path}
          style={pad}
          href={wikiPath(project.id, node.page.id)}
          menu={wikiMenu}
        />
      );
    }
    if (node.type === "wiki") {
      return (
        <span className="flex items-start gap-1 py-2 pr-1.5 text-sm" style={pad}>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <Link
              href={wikiPath(project.id, node.page.id)}
              tabIndex={-1}
              {...(conversation && {
                onClick: (event: ReactMouseEvent) => {
                  if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
                    event.preventDefault();
                    openWikiPanel({
                      kind: "page",
                      pageId: node.page.id.toLowerCase(),
                      version: null,
                    });
                  }
                },
              })}
              className="flex min-w-0 items-start font-medium break-words text-fg underline-offset-4 hover:underline"
            >
              {leading}
              <span id={`${id}-${row.id}-name`} className="min-w-0">
                {title}
              </span>
            </Link>
            <span id={`${id}-${row.id}-details`} className="flex min-w-0 flex-col gap-0.5">
              <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted tabular-nums">
                {[t("knowledgePanel.type.wiki"), formatDate(node.page.updatedAt)].join(" · ")}
                {/* As the Pages row (#411): an icon, its words in the row's description (#426). */}
                {node.page.declassified && can(project, "declassify") ? (
                  <>
                    <ShieldAlert
                      aria-hidden="true"
                      data-declassified-page=""
                      className="size-3.5 shrink-0 text-warning-fg"
                      strokeWidth={1.75}
                    />
                    <span className="sr-only">, {t("wiki.declassified")}</span>
                  </>
                ) : null}
              </span>
              {path}
            </span>
          </span>
          {wikiMenu}
        </span>
      );
    }
    return (
      <span className="flex py-2 pr-1.5" style={pad}>
        <FileRowBody
          document={node.document}
          facts={facts(node.document, { ...menuOf(row), extra: moveItem })}
          ocrMaxPages={fileSettings.ocrMaxPages}
          leading={leading}
          title={title}
          titleId={`${id}-${row.id}-name`}
          detailsId={`${id}-${row.id}-details`}
          after={path}
          columns={props.columns}
          density={scope === "all" ? "panel" : "full"}
        />
      </span>
    );
  };

  // Only rows that render a menu say so (their description) and open one on Shift+F10.
  const hasMenu = (node: TreeNode) => {
    if (node.kind === "section") {
      // As `SectionMenu` renders: Wiki always (Open the wiki), Files with an action only.
      return (
        node.section === "wiki" ||
        (node.section === "files" && (canManage || (canUpload && props.upload !== null)))
      );
    }
    if (node.kind === "folder") {
      return canManage || (canUpload && node.section === "files");
    }
    if (node.type === "wiki") {
      return canManage;
    }
    if (node.type === "attachment") {
      // Details are always offered, beside whatever the viewer may do.
      return true;
    }
    if (node.type === "artefact") {
      return true;
    }
    const level = fileSettings.scheme.find((l) => l.id === node.document.classification);
    const actions = fileActions(node.document, project, userId, level);
    return actions.length > 0 || (canManage && node.type === "file");
  };

  const drilledNode = drilled ? tree.byId.get(drilled) : undefined;
  // The restricted count belongs to Files (Sources): on its section row wherever that row sits,
  // else on its own line (the Sources tab, a search without file rows, restricted files only).
  const filesRowShown = rows.some((r) => r.id === sectionId("files"));
  const drilledWiki = drilledNode?.section === "wiki";
  const restrictedApart =
    restricted && (noSources || !filesRowShown) && !drilledWiki ? (
      <p className="px-2 pt-2 text-xs text-fg-muted">
        {t("files.restricted", { count: restricted })}
      </p>
    ) : null;
  // Root folders on the project home, which has no section row to hold "New folder".
  const scopedCreate =
    scope !== "all" &&
    canManage &&
    !(drilledNode?.kind === "folder" && drilledNode.depth >= MAX_FOLDER_DEPTH) ? (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          opener.current = null;
          landing.current = null;
          setDialog({
            kind: "create",
            section: scope,
            parent: drilledNode?.kind === "folder" ? drilledNode : null,
          });
        }}
      >
        <FolderPlus aria-hidden="true" strokeWidth={1.75} />
        {t("folders.new")}
      </Button>
    ) : null;
  const toolbar =
    props.toolbar || scopedCreate ? (
      <>
        {scopedCreate}
        {props.toolbar}
      </>
    ) : null;
  const label = scope === "all" ? t("knowledgePanel.tree") : t(`knowledgePanel.section.${scope}`);

  return (
    <div data-esi-content="files" ref={container} className="flex min-h-0 flex-1 flex-col">
      {/* Read once by every row with a menu: how to reach the actions from the keyboard. */}
      <span id={`${id}-menu-hint`} className="sr-only">
        {t("knowledgePanel.actionsHint")}
      </span>
      {noSources && !toolbar ? null : (
        <div
          className={cn(
            "flex shrink-0 gap-2 px-4 pt-3 pb-2",
            scope === "wiki" ? "flex-col items-start" : "items-center",
          )}
        >
          <div
            className={cn(
              "relative min-w-0 flex-1",
              scope === "wiki" && "w-full",
              scope !== "all" && "max-w-sm",
              noSources && "hidden",
            )}
          >
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
              strokeWidth={1.75}
            />
            <Input
              type="search"
              value={query}
              aria-label={t(
                scope === "wiki" ? "knowledgePanel.searchPages" : "knowledgePanel.search",
              )}
              placeholder={t(
                scope === "wiki" ? "knowledgePanel.searchPages" : "knowledgePanel.search",
              )}
              onFocus={() => {
                if (!query) {
                  beforeSearch.current = focusedId;
                }
              }}
              onChange={(e) => {
                // Each query opens its own paths: toggles made for the previous one are dropped.
                setSearchOpen(new Map());
                setQuery(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape" && query) {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery("");
                  setSearchOpen(new Map());
                  setFocusedId(beforeSearch.current);
                }
              }}
              className="h-8 pl-8 text-sm"
            />
          </div>
          {toolbar ? (
            <div
              className={cn(
                "flex shrink-0 items-center gap-1",
                scope !== "wiki" && !noSources && "ml-auto",
              )}
            >
              {toolbar}
            </div>
          ) : null}
        </div>
      )}
      {/* The match count follows the query for assistive technology, without moving focus. */}
      <p className="sr-only" aria-live="polite">
        {search ? t("knowledgePanel.matches", { count: shownMatches }) : ""}
      </p>

      {failed.map(([name, e]) => (
        <div key={name} className="px-4 pb-2">
          <Notice tone="danger">{e.message}</Notice>
        </div>
      ))}

      {drilledNode?.kind === "folder" && !search ? (
        <Breadcrumb
          tree={tree}
          node={drilledNode}
          sectionName={sectionName(drilledNode.section)}
          onBack={back}
          backRef={backButton}
          onOpen={(to) => {
            // Focus lands on the step of the path that the new root lists.
            const path = [...folderPath(tree, drilledNode), drilledNode];
            const step = to ? path.findIndex((f) => f.id === to.id) + 1 : 0;
            drill(to, path[step]?.id ?? drilledNode.id);
          }}
        />
      ) : null}

      {!loaded ? (
        <div className="flex flex-col gap-2 px-4" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : noSources && props.empty ? (
        <div className="px-4 pt-2">
          {props.empty}
          {restrictedApart}
        </div>
      ) : noSources ? (
        <div className="px-4 pt-4">
          <EmptyState
            icon={Library}
            headingLevel={3}
            title={t("knowledgePanel.empty.title")}
            // Drafts may wait for review with no published page: the wiki stays reachable.
            action={
              showWiki ? (
                <WikiLine projectId={project.id} pending={canReview ? pending : 0} />
              ) : undefined
            }
          >
            {canUpload ? t("knowledgePanel.empty.upload") : t("knowledgePanel.empty.body")}
          </EmptyState>
          {restrictedApart}
        </div>
      ) : drilledNode?.kind === "folder" && !search && rows.length === 0 ? (
        <p className="px-4 pt-3 text-sm text-fg-secondary">{t("knowledgePanel.emptyFolder")}</p>
      ) : search && rows.length === 0 && only && !query.trim() && props.onlyEmpty ? (
        <div className="px-4 pt-4">{props.onlyEmpty}</div>
      ) : search && rows.length === 0 ? (
        <div className="px-4 pt-4">
          <EmptyState
            icon={Search}
            headingLevel={3}
            title={t("knowledgePanel.noMatch.title")}
            action={
              <Button
                size="sm"
                variant="ghost"
                className="-ml-2"
                onClick={() => {
                  setQuery("");
                  setSearchOpen(new Map());
                }}
              >
                {t("knowledgePanel.noMatch.clear")}
              </Button>
            }
          >
            {t("knowledgePanel.noMatch.body", { query: query.trim() })}
          </EmptyState>
          {restrictedApart}
        </div>
      ) : (
        // The page titles' tooltips (#411), one provider: the tree also renders outside the app's.
        <TooltipProvider delayDuration={300}>
          <TreeView
            label={label}
            rows={rows}
            focusedId={focusedId}
            onFocusChange={setFocusedId}
            onToggle={(row, on) => {
              if (row.node.kind === "folder" && narrow && on && !search) {
                drill(row.node);
              } else {
                toggle(row.node, on);
              }
            }}
            onActivate={activate}
            onExpandSiblings={(row) => {
              const siblings = rows.filter(
                (r) =>
                  r.node.parentId === row.node.parentId &&
                  r.expanded === false &&
                  // Narrow screens drill into folders instead of expanding them.
                  !(narrow && !search && r.node.kind === "folder"),
              );
              if (search) {
                setSearchOpen((was) => {
                  const next = new Map(was);
                  for (const r of siblings) {
                    next.set(r.id, true);
                  }
                  return next;
                });
              } else {
                if (siblings.some((r) => r.node.section === "attachments")) {
                  setAttachmentOpen(true);
                }
                keep({ expanded: [...state.expanded, ...siblings.map((r) => r.id)] });
              }
            }}
            onMenu={(row) => (hasMenu(row.node) ? setMenuFor(row.id) : undefined)}
            onBack={drilled && !search ? back : undefined}
            nameOf={(row) =>
              row.node.kind === "section" ? sectionName(row.node.section) : row.node.name
            }
            itemProps={(row) => ({
              "aria-labelledby": `${id}-${row.id}-name`,
              "aria-describedby": [
                `${id}-${row.id}-details`,
                row.node.id === sectionId("files") && restricted
                  ? `${id}-${row.id}-details-restricted`
                  : null,
                hasMenu(row.node) ? `${id}-menu-hint` : null,
              ]
                .filter(Boolean)
                .join(" "),
              ...(menuFor === row.id && { "data-menu": "open" as const }),
              ...(props.selectedId !== undefined && row.node.kind === "item"
                ? { "aria-selected": row.node.id === props.selectedId }
                : {}),
              ...(props.linkedId &&
                row.node.id === props.linkedId && { "aria-current": "true" as const }),
            })}
            estimateSize={(row) =>
              row.node.kind === "section"
                ? row.node.section === "files" && restricted
                  ? 56
                  : 40
                : row.node.kind === "folder"
                  ? 32
                  : row.node.type === "attachment"
                    ? 56
                    : row.node.type === "wiki" && scope === "wiki"
                      ? search
                        ? 52
                        : 32
                      : row.node.type === "wiki" || row.node.type === "artefact"
                        ? search
                          ? 72
                          : 56
                        : props.columns
                          ? 56
                          : search
                            ? 112
                            : 96
            }
            rowClassName={(row) =>
              cn(
                "rounded-md transition-colors duration-(--duration-fast)",
                row.node.kind === "section" ? "mt-1 first:mt-0" : "hover:bg-surface-hover/60",
                props.selectedId &&
                  row.node.id === props.selectedId &&
                  "bg-surface-selected [&_.text-fg-muted]:text-fg-secondary",
                props.linkedId &&
                  row.node.id === props.linkedId &&
                  "bg-accent-subtle hover:bg-accent-subtle",
                row.node.kind === "folder" && "cursor-default",
              )
            }
            renderRow={(row) => renderRow(row)}
            reveal={reveal}
            scrollRef={props.scrollRef}
            className={cn("px-2 pb-4", props.scrollRef ? "" : "flex-1")}
            after={restrictedApart}
          />
        </TooltipProvider>
      )}

      {overlays}
      {folderList ? (
        <FolderDialogs
          projectId={project.id}
          tree={tree}
          folders={folderList}
          dialog={dialog}
          onClose={() => setDialog(null)}
          onDone={onDone}
          returnFocus={dialogReturn}
        />
      ) : null}
    </div>
  );
}

function parentFolderId(tree: Tree, node: TreeNode): string | null {
  const parent = node.parentId ? tree.byId.get(node.parentId) : undefined;
  return parent?.kind === "folder" ? parent.folder.id : null;
}

/** The disclosure mark of a branch; an empty folder keeps its place without one. */
function Chevron(props: { expanded?: boolean; empty?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid size-5 shrink-0 place-items-center text-fg-muted",
        props.empty && "invisible",
      )}
    >
      <ChevronRight
        className={cn(
          "size-3.5 transition-transform duration-(--duration-fast)",
          props.expanded && "rotate-90",
        )}
        strokeWidth={1.75}
      />
    </span>
  );
}

/** The matched part of a name, marked (any case or accent). */
function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim();
  if (!needle) {
    return <>{text}</>;
  }
  // Folding keeps one character per character for the scripts titles use (NFD marks dropped
  // from the composed form): find the match on the folded text, cut the original there.
  const folded = Array.from(text).map(foldTitle);
  const target = foldTitle(needle);
  const chars = Array.from(text);
  for (let start = 0; start < folded.length; start++) {
    let joined = "";
    for (let end = start; end < folded.length; end++) {
      joined += folded[end];
      if (joined === target) {
        return (
          <>
            {chars.slice(0, start).join("")}
            <mark className="rounded-[3px] bg-(--mark-bg) px-px text-(--mark-fg)">
              {chars.slice(start, end + 1).join("")}
            </mark>
            {chars.slice(end + 1).join("")}
          </>
        );
      }
      if (!target.startsWith(joined)) {
        break;
      }
    }
  }
  return <>{text}</>;
}

/** A section's header row, shared with the standalone conversation's files sheet. */
export function SectionRow(props: {
  row: Row;
  name: string;
  labelId: string;
  detailsId: string;
  restricted: number | null;
  wiki: { projectId: string; pending: number } | null;
  menu: ReactNode;
}) {
  const { t } = useTranslation();
  const { row } = props;
  const node = row.node;
  const Icon = node.kind === "section" ? SECTION_ICON[node.section] : FileText;
  return (
    <span className="flex flex-col gap-0.5 py-1.5 pr-1.5 pl-2">
      <span className="flex min-h-7 items-center gap-1.5">
        <Chevron expanded={row.expanded} />
        <Icon aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.5} />
        <span id={props.labelId} className="min-w-0 truncate text-sm font-semibold text-fg">
          {props.name}
        </span>
        <span id={props.detailsId} className="flex min-w-0 flex-1 items-center gap-2">
          <span className="text-xs text-fg-muted tabular-nums">
            <span aria-hidden="true">{row.count}</span>
            <span className="sr-only">{t("knowledgePanel.items", { count: row.count ?? 0 })}</span>
          </span>
          {props.wiki && props.wiki.pending > 0 ? (
            <Link
              href={wikiChangesPath(props.wiki.projectId)}
              tabIndex={-1}
              className="ml-auto truncate text-xs text-accent-text underline-offset-4 hover:underline"
            >
              {t("wiki.reviewCount", { count: props.wiki.pending })}
            </Link>
          ) : null}
        </span>
        {props.wiki ? (
          <Button asChild size="xs" variant="ghost" className="shrink-0 text-fg-secondary">
            <Link href={wikiPath(props.wiki.projectId)} tabIndex={-1}>
              {t("wiki.openWiki")}
              <ArrowUpRight aria-hidden="true" strokeWidth={1.75} />
            </Link>
          </Button>
        ) : null}
        {props.menu}
      </span>
      {/* Under any expansion or search: files the viewer cannot read, never named (design §8). */}
      {props.restricted ? (
        <span id={`${props.detailsId}-restricted`} className="pl-12 text-xs text-fg-muted">
          {t("files.restricted", { count: props.restricted })}
        </span>
      ) : null}
    </span>
  );
}

type MenuControl = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocus: () => HTMLElement | null;
  label: string;
};

/** A row's "…" button and its menu: no tab stop (the row is), opened by Shift+F10 too. */
export function ItemMenu(props: MenuControl & { children: ReactNode }) {
  return (
    <DropdownMenu open={props.open} onOpenChange={props.onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          tabIndex={-1}
          aria-label={props.label}
          className="shrink-0 text-fg-muted opacity-100 transition-opacity duration-(--duration-fast) hover:text-fg [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/item:opacity-100 [@media(hover:hover)]:group-focus-within/item:opacity-100 [@media(hover:hover)]:group-data-[menu=open]/item:opacity-100"
        >
          <Ellipsis aria-hidden="true" strokeWidth={1.75} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-44"
        onCloseAutoFocus={(event) => {
          const row = props.returnFocus();
          if (row) {
            event.preventDefault();
            row.focus();
          }
        }}
      >
        {props.children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SectionMenu(
  props: MenuControl & {
    section: TreeSection;
    canManage: boolean;
    canUpload: boolean;
    pending: number;
    projectId: string;
    onCreate: () => void;
    onUpload: () => void;
  },
) {
  const { t } = useTranslation();
  const wiki = props.section === "wiki";
  if (!props.canManage && !props.canUpload && !wiki) {
    return null;
  }
  return (
    <ItemMenu {...props}>
      {props.canManage ? (
        <DropdownMenuItem onSelect={props.onCreate}>
          <FolderPlus aria-hidden="true" strokeWidth={1.75} />
          {t("folders.new")}
        </DropdownMenuItem>
      ) : null}
      {props.canUpload ? (
        <DropdownMenuItem onSelect={props.onUpload}>
          <Upload aria-hidden="true" strokeWidth={1.75} />
          {t("files.upload")}
        </DropdownMenuItem>
      ) : null}
      {wiki ? (
        <DropdownMenuItem asChild>
          <Link href={wikiPath(props.projectId)}>
            <ArrowUpRight aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.openWiki")}
          </Link>
        </DropdownMenuItem>
      ) : null}
      {wiki && props.pending > 0 ? (
        <DropdownMenuItem asChild>
          <Link href={wikiChangesPath(props.projectId)}>
            <ListChecks aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.reviewCount", { count: props.pending })}
          </Link>
        </DropdownMenuItem>
      ) : null}
    </ItemMenu>
  );
}

function FolderMenu(
  props: MenuControl & {
    node: FolderNode;
    canManage: boolean;
    canUpload: boolean;
    onCreate: () => void;
    onRename: () => void;
    onMove: () => void;
    onDelete: () => void;
    onUpload: () => void;
  },
) {
  const { t } = useTranslation();
  return (
    <ItemMenu {...props}>
      {props.canUpload ? (
        <DropdownMenuItem onSelect={props.onUpload}>
          <Upload aria-hidden="true" strokeWidth={1.75} />
          {t("folders.uploadHere")}
        </DropdownMenuItem>
      ) : null}
      {props.canManage ? (
        <>
          {props.node.depth < MAX_FOLDER_DEPTH ? (
            <DropdownMenuItem onSelect={props.onCreate}>
              <FolderPlus aria-hidden="true" strokeWidth={1.75} />
              {t("folders.new")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={props.onRename}>
            <Pencil aria-hidden="true" strokeWidth={1.75} />
            {t("folders.rename")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={props.onMove}>
            <FolderInput aria-hidden="true" strokeWidth={1.75} />
            {t("folders.moveTo")}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={props.onDelete}>
            <Trash2 aria-hidden="true" strokeWidth={1.75} />
            {t("folders.delete.action")}
          </DropdownMenuItem>
        </>
      ) : null}
    </ItemMenu>
  );
}

/** A drilled folder's path with a way back (narrow screens and the sheet, design §6). */
function Breadcrumb(props: {
  tree: Tree;
  node: FolderNode;
  sectionName: string;
  onBack: () => void;
  backRef: RefObject<HTMLButtonElement | null>;
  onOpen: (to: FolderNode | null) => void;
}) {
  const { t } = useTranslation();
  const ancestors = folderPath(props.tree, props.node);
  const parent = ancestors.at(-1);
  return (
    <nav
      aria-label={t("knowledgePanel.breadcrumb")}
      className="flex shrink-0 flex-col gap-1 border-b border-line px-4 pb-2"
    >
      <Button
        size="xs"
        variant="ghost"
        ref={props.backRef}
        className="-ml-2 self-start text-fg-secondary"
        onClick={props.onBack}
      >
        <ArrowLeft aria-hidden="true" strokeWidth={1.75} />
        {t("knowledgePanel.back", { name: parent?.name ?? props.sectionName })}
      </Button>
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 text-xs text-fg-muted">
        <li>
          <button
            type="button"
            className="underline-offset-4 hover:text-fg hover:underline"
            onClick={() => props.onOpen(null)}
          >
            {props.sectionName}
          </button>
        </li>
        {ancestors.map((f) => (
          <li key={f.id} className="flex min-w-0 items-center gap-x-1">
            <span aria-hidden="true">›</span>
            <button
              type="button"
              className="max-w-[10rem] truncate underline-offset-4 hover:text-fg hover:underline"
              onClick={() => props.onOpen(f)}
            >
              {f.name}
            </button>
          </li>
        ))}
        <li className="flex min-w-0 items-center gap-x-1">
          <span aria-hidden="true">›</span>
          <span aria-current="page" className="truncate font-medium text-fg">
            {props.node.name}
          </span>
        </li>
      </ol>
    </nav>
  );
}

/** The way into the full wiki, with the review queue count when something waits. */
export function WikiLine(props: { projectId: string; pending: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1", props.className)}>
      {props.pending > 0 ? (
        <Link
          href={wikiChangesPath(props.projectId)}
          className="text-sm text-accent-text underline-offset-4 hover:underline"
        >
          {t("wiki.reviewCount", { count: props.pending })}
        </Link>
      ) : null}
      <Button asChild size="sm" variant="outline">
        <Link href={wikiPath(props.projectId)}>
          {t("wiki.openWiki")}
          <ArrowUpRight aria-hidden="true" strokeWidth={1.75} />
        </Link>
      </Button>
    </div>
  );
}

/** Members and principals the upload dialog needs, fetched once someone may upload. */
export function useUploadDialog(project: Project) {
  const canUpload = canChange(project, "file.upload");
  const documents = useDocuments(project.id);
  const settings = useFileSettings(project, documents.data);
  const members = useMembers(project.id, canUpload);
  const principals = usePrincipals(canUpload);
  const [target, setTarget] = useState<{ folderId: string | null } | null>(null);
  const dialog = settings.data ? (
    <UploadDialog
      open={target !== null}
      onClose={() => setTarget(null)}
      project={project}
      settings={settings.data}
      members={members.data}
      principals={principals.data}
      folderId={target?.folderId ?? null}
    />
  ) : null;
  return {
    upload: canUpload ? { open: (folderId: string | null) => setTarget({ folderId }) } : null,
    ready: settings.data !== undefined,
    dialog,
  };
}

/** The page's own scroll area (`data-scroll-owner="page"`): long trees scroll with the page. */
export const PAGE_SCROLL = {
  get current() {
    return document.querySelector<HTMLElement>('[data-scroll-owner="page"]');
  },
};

/** `?folder=<id>` of the current page, replaced (not pushed) as folders open. */
export function useFolderParam(): [string | null, (folderId: string | null) => void] {
  const search = useSearch();
  const folder = search.get("folder");
  const set = (next: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (next) {
      params.set("folder", next);
    } else {
      params.delete("folder");
    }
    const query = params.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ""}`, { replace: true });
  };
  return [folder, set];
}

/**
 * A page of the wiki's own Pages panel (#411): one line, the title truncated, then its marks
 * (declassified, a pending change). No "Wiki page · date" line (user decision): the full title
 * and the date show in a tooltip on hover and while the row has focus, and the accessible name
 * and description keep them.
 */
function WikiPageRow(props: {
  name: string;
  title: ReactNode;
  leading: ReactNode;
  nameId: string;
  detailsId: string;
  updated: string;
  declassified: boolean;
  pending: boolean;
  path: ReactNode;
  style: CSSProperties;
  href: string;
  menu: ReactNode;
}) {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLSpanElement>(null);
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  // Escape (or the pointer leaving) dismisses it until the next hover or focus (WCAG 1.4.13).
  const [dismissed, setDismissed] = useState(false);
  // Real focus on the row, not the tree's roving tab stop (which stays when focus leaves).
  useEffect(() => {
    const item = nameRef.current?.closest<HTMLElement>("[role='treeitem']");
    if (!item) {
      return;
    }
    // Keyboard focus only (`:focus-visible`): a click or a reveal after a move opens nothing.
    const visible = () => {
      try {
        return item.matches(":focus-visible");
      } catch {
        return true;
      }
    };
    const sync = (event?: FocusEvent) => {
      setFocused(event ? event.type === "focus" && visible() : false);
      setDismissed(false);
    };
    sync();
    item.addEventListener("focus", sync);
    item.addEventListener("blur", sync);
    return () => {
      item.removeEventListener("focus", sync);
      item.removeEventListener("blur", sync);
    };
  }, []);
  return (
    <span className="flex min-h-8 items-center gap-1 py-1 pr-1.5 text-sm" style={props.style}>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <Tooltip
            open={(hover || focused) && !dismissed}
            onOpenChange={(open) => {
              setHover(open);
              setDismissed(!open);
            }}
            disableHoverableContent
          >
            <TooltipTrigger asChild>
              <Link
                href={props.href}
                tabIndex={-1}
                className="flex min-w-0 items-center font-medium text-fg underline-offset-4 hover:underline"
              >
                {props.leading}
                <span ref={nameRef} id={props.nameId} className="min-w-0 truncate">
                  {props.title}
                </span>
              </Link>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              align="start"
              className="max-w-xs"
              data-esi-content="wiki"
              data-page-title-tooltip=""
            >
              <span className="block break-words">{props.name}</span>
              <span className="block text-background/70 tabular-nums">
                {t("knowledgePanel.updated", { date: props.updated })}
              </span>
            </TooltipContent>
          </Tooltip>
          {props.declassified ? (
            <ShieldAlert
              aria-hidden="true"
              data-declassified-page=""
              className="size-3.5 shrink-0 text-warning-fg"
              strokeWidth={1.75}
            />
          ) : null}
          {props.pending ? (
            <GitPullRequestArrow
              aria-hidden="true"
              data-pending-page=""
              className="size-3.5 shrink-0 text-warning-fg"
              strokeWidth={1.75}
            />
          ) : null}
        </span>
        <span id={props.detailsId} className="flex min-w-0 flex-col">
          <span className="sr-only">
            {[
              t("knowledgePanel.updated", { date: props.updated }),
              props.declassified ? t("wiki.declassified") : null,
              props.pending ? t("knowledgePanel.pendingPage") : null,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
          {props.path}
        </span>
      </span>
      {props.menu}
    </span>
  );
}
